import {
  useInfiniteQuery,
  useQuery,
  QueryKey,
  UseQueryOptions,
  QueryFunction,
  useMutation,
  MutationFunction,
  UseMutationOptions,
} from '@tanstack/react-query';
import { useAppSelector } from '../stores';
import { base } from 'api';

function useAuthQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >,
) {
  const { accessToken } = useAppSelector((state) => state.auth);
  if (accessToken) {
    base.defaults.headers.common['authorization'] = `Bearer ${accessToken}`;
  }

  const enabled = options?.enabled === undefined ? true : options.enabled;

  const { refetch, ...rest } = useQuery({
    ...options,
    queryKey,
    queryFn,
    enabled: enabled && !!accessToken,
  });

  return { refetch, ...rest };
}

function useAuthMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) {
  const { accessToken } = useAppSelector((state) => state.auth);
  if (accessToken) {
    base.defaults.headers.common['authorization'] = `Bearer ${accessToken}`;
  }

  return useMutation({
    ...options,
    mutationFn,
  });
}

interface PagedResponse<T> {
  data: T[];
  totalPages: number;
}

function useAuthInfiniteList<T>(
  queryKey: QueryKey,
  fetchPage: (page: number) => Promise<{ data: PagedResponse<T> }>,
  enabled = true,
) {
  const { accessToken } = useAppSelector((state) => state.auth);
  if (accessToken) {
    base.defaults.headers.common['authorization'] = `Bearer ${accessToken}`;
  }

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => (await fetchPage(pageParam)).data,
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      pages.length < (last.totalPages ?? 0) ? pages.length + 1 : undefined,
    select: (data) => data.pages.flatMap((p) => p.data ?? []),
    enabled: enabled && !!accessToken,
  });
}

export * from '@tanstack/react-query';
export { useAuthQuery, useAuthMutation, useAuthInfiniteList };
